import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useVisitorId } from '../hooks/useVisitorId';
import Cookies from 'js-cookie';
import { COOKIE_NAME, BACKEND_URL } from '../config';
import clientLogger from '../utils/clientLogger';
import { prysmDB } from '../utils/database';
import apiService from '../services/apiService';
import squareConfig from '../config/squareConfig';

function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: 'User Name', email: 'user@example.com' });
  const userMenuRef = useRef(null);
  const visitorId = useVisitorId();

  // Listen for user info updates
  useEffect(() => {
    const handleUserInfoUpdate = (event) => {
      setUserInfo(event.detail);
    };

    window.addEventListener('userInfoUpdate', handleUserInfoUpdate);
    return () => {
      window.removeEventListener('userInfoUpdate', handleUserInfoUpdate);
    };
  }, []);

  // Load initial user info from storage
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const registrationData = await prysmDB.get('registration_data', 'current_registration');
        if (registrationData) {
          setUserInfo({
            name: `${registrationData.first_name || registrationData.firstName} ${registrationData.last_name || registrationData.lastName}`,
            email: registrationData.email
          });
        }
      } catch (error) {
        console.error('[Navbar] Error loading user info from storage:', error);
      }
    };

    loadUserInfo();
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleResetSession = async () => {
    try {
      // Show confirmation dialog
      const confirmed = window.confirm(
        'Are you sure you want to reset your session? This will clear all your data, including any payments you have made and start a new registration process.'
      );

      if (!confirmed) {
        return;
      }

      // Close user menu
      setIsUserMenuOpen(false);

      // Get current visitor UUID for server callback
      const currentUUID = visitorId;

      // Completely delete the storage database
      try {
        await prysmDB.deleteDatabase();
        console.log('[Navbar] storage database completely deleted');
      } catch (error) {
        console.error('[Navbar] Error deleting storage database:', error);
        // Log the error but don't fail the reset process
        clientLogger.logError('navbar_reset_storage', `Failed to delete storage database during navbar reset: ${error.message}`, error, {
          context: 'navbar_reset_delete_storage'
        });
      }

      // Clear all localStorage items
      const localStorageKeys = [
        'adobeCleanFontAdded',
        'auth_token',
        'visitorUUID',
        'registration_data',
        'clientLogger_failedLogs',
        'square-debug'
      ];
      localStorageKeys.forEach(key => {
        localStorage.removeItem(key);
      });

      // Clear any remaining localStorage items that might contain our data
      // This is a safety net to catch any items we might have missed
      const allLocalStorageKeys = Object.keys(localStorage);
      allLocalStorageKeys.forEach(key => {
        // Clear any keys that might be related to our application
        if (key.includes('prysm') || key.includes('registration') || key.includes('square') ||
          key.includes('selfie') || key.includes('license') || key.includes('sms') ||
          key.includes('visitor') || key.includes('clientLogger')) {
          localStorage.removeItem(key);
          console.log(`[Navbar] Cleared additional localStorage key: ${key}`);
        }
      });

      // Clear all sessionStorage data
      sessionStorage.clear();

      // Clear cookies
      Cookies.remove(COOKIE_NAME);

      // Call server to reset session
      if (BACKEND_URL) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/session/reset`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.uuid) {
              // Set the new UUID from server
              const { setVisitorUUID } = await import('../utils/uuidManager');
              await setVisitorUUID(result.uuid);
              console.log('Server session reset completed with new UUID:', result.uuid);
            } else {
              console.log('Server session reset completed:', response.ok);
            }
          }
        } catch (serverError) {
          console.error('Server reset failed, but local reset completed:', serverError);

          // Log API error to server
          clientLogger.logApiError('/api/session/reset', serverError, {
            action: 'reset_session',
            method: 'POST',
            visitorUUID: currentUUID
          });
        }
      }

      // Dispatch events to notify components of reset
      window.dispatchEvent(new CustomEvent('sessionReset'));
      window.dispatchEvent(new CustomEvent('uuidUpdated'));

      // Redirect to welcome page to trigger new session creation
      window.location.href = '/';

    } catch (error) {
      console.error('Error during session reset:', error);
      alert('An error occurred during session reset. Please refresh the page manually.');
    }
  };

  const handleAboutClick = () => {
    setIsUserMenuOpen(false);
    setIsAboutModalOpen(true);
  };

  const closeAboutModal = () => {
    setIsAboutModalOpen(false);
  };

  const handleSupportClick = () => {
    setIsSupportModalOpen(true);
    setIsMobileMenuOpen(false); // Close mobile menu if open
  };

  const closeSupportModal = () => {
    setIsSupportModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* Custom styles for about modal animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>

      <nav className="bg-gray-900 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <a href="https://public.ourprysm.com" target="_blank" rel="noopener noreferrer" className="flex items-center">
                  <img
                    src="/prysmtop.png"
                    alt="OurPrysm"
                    className="h-8 w-auto object-contain"
                  />
                </a>
              </div>
              {/* Desktop Menu */}
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <Link
                  to="/"
                  className="border-transparent text-gray-300 hover:text-white hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Home
                </Link>
                <Link
                  to="/register"
                  className="hidden border-transparent text-gray-300 hover:text-white hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Register
                </Link>
                <Link
                  to="/upload-selfie"
                  className="hidden border-transparent text-gray-300 hover:text-white hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Upload Selfie
                </Link>
                <Link
                  to="/upload-license"
                  className="hidden border-transparent text-gray-300 hover:text-white hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Upload License
                </Link>
                <Link
                  to="/checkout"
                  className="hidden border-transparent text-gray-300 hover:text-white hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Checkout
                </Link>
                <Link
                  to="/payment"
                  className="hidden border-transparent text-gray-300 hover:text-white hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Payment
                </Link>
                <Link
                  to="/square-payment"
                  className="hidden border-transparent text-gray-300 hover:text-white hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Square Payment
                </Link>
                <button
                  onClick={handleSupportClick}
                  className="border-transparent text-gray-300 hover:text-white hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200"
                >
                  Support
                </button>
                <Link
                  to="/support-qa"
                  className="border-transparent text-gray-300 hover:text-white hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200"
                >
                  Support Q/A
                </Link>
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center" ref={userMenuRef}>
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                  id="user-menu-button"
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {userInfo.name.charAt(0)}
                  </div>
                </button>

                {/* Dropdown menu */}
                {isUserMenuOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-64 rounded-xl shadow-lg py-1 bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none transition-all duration-300 ease-in-out transform opacity-100 scale-100">
                    <div className="px-4 py-3 border-b border-gray-700">
                      <p className="text-sm font-medium text-gray-200">{userInfo.name}</p>
                      <p className="text-xs text-gray-400">{userInfo.email}</p>
                      <div className="mt-2 p-2 bg-gray-900 rounded-lg">
                        <p className="text-xs font-medium text-gray-400 mb-1">UUID</p>
                        <p className="text-xs font-mono text-gray-300 break-all bg-gray-800 p-2 rounded border border-gray-700">
                          {visitorId || 'Loading...'}
                        </p>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={handleAboutClick}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        About
                      </button>
                      <button
                        onClick={handleResetSession}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reset Session
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center sm:hidden">
              <button
                onClick={toggleMobileMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-pink-500"
              >
                <span className="sr-only">Open main menu</span>
                <svg
                  className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <svg
                  className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} sm:hidden transition-all duration-300 ease-in-out`}>
          <div className="pt-2 pb-3 space-y-1">
            <Link
              to="/"
              className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 hover:border-gray-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/register"
              className="hidden block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 hover:border-gray-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Register
            </Link>
            <Link
              to="/upload-selfie"
              className="hidden block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 hover:border-gray-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Upload Selfie
            </Link>
            <Link
              to="/upload-license"
              className="hidden block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 hover:border-gray-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Upload License
            </Link>
            <Link
              to="/checkout"
              className="hidden block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 hover:border-gray-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Checkout
            </Link>
            <Link
              to="/payment"
              className="hidden block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 hover:border-gray-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Payment
            </Link>
            <Link
              to="/square-payment"
              className="hidden block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 hover:border-gray-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Square Payment
            </Link>
            <button
              onClick={handleSupportClick}
              className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 hover:border-gray-300 transition-colors duration-200"
            >
              Support
            </button>
            <Link
              to="/support-qa"
              className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 hover:border-gray-300 transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Support Q/A
            </Link>
          </div>
        </div>

        {/* About Modal */}
        {isAboutModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-lg w-full border border-gray-700/50 backdrop-blur-sm animate-slideUp">
              {/* Header with close button */}
              <div className="flex justify-between items-center p-6 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white">About</h2>
                </div>
                <button
                  onClick={closeAboutModal}
                  className="text-gray-400 hover:text-white transition-colors duration-200 p-2 hover:bg-gray-700/50 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 pb-6">
                {/* App Info Section */}
                <div className="mb-8">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {process.env.REACT_APP_COMPANY_NAME || 'OurPrysm'}
                    </h3>
                    <p className="text-gray-400 text-sm">Secure Registration Platform</p>
                  </div>

                  {/* Version and Build Info */}
                  <div className="bg-gray-800/50 rounded-xl p-4 mb-6 border border-gray-700/30">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400 block">Version</span>
                        <span className="text-white font-medium">{process.env.REACT_APP_VERSION || '1.0.0'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block">Environment</span>
                        <span className={`font-medium ${squareConfig.isProduction() ? 'text-green-400' : 'text-yellow-400'}`}>
                          {squareConfig.isProduction() ? 'Production' : 'Sandbox'}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-400 block">Last Build</span>
                        <span className="text-white font-medium text-xs">{new Date().toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Team Section */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-white mb-4">Development Team</h4>
                  <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/30">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2">
                        <span className="text-gray-400">Developer</span>
                        <span className="text-white font-medium">{process.env.REACT_APP_DEVELOPER_NAME || 'Development Team'}</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-gray-400">Designer</span>
                        <span className="text-white font-medium">{process.env.REACT_APP_DESIGNER_NAME || 'Design Team'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Section */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Contact</h4>
                  <div className="bg-gray-800/30 rounded-xl p-4 border border-gray-700/30">
                    <a
                      href={`mailto:${process.env.REACT_APP_ABOUT_CONTACT || 'contact@ourprysm.com'}`}
                      className="text-blue-400 hover:text-blue-300 transition-colors duration-200 font-medium flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>{process.env.REACT_APP_ABOUT_CONTACT || 'contact@ourprysm.com'}</span>
                    </a>
                  </div>
                </div>

                {/* Footer */}
                <div className="text-center pt-4 border-t border-gray-700/30">
                  <p className="text-gray-400 text-sm">
                    © {new Date().getFullYear()} {process.env.REACT_APP_COMPANY_NAME || 'OurPrysm'}. All rights reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Support Modal */}
        {isSupportModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Support</h2>
                <button
                  onClick={closeSupportModal}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Need Help?</h3>
                  <p className="text-gray-400 mb-6">
                    We're here to help! If you're experiencing any issues or have questions about the registration process, please don't hesitate to reach out to our support team.
                  </p>
                </div>

                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center justify-center space-x-3">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-300 font-medium">Contact Support:</span>
                  </div>
                  <div className="mt-3 text-center">
                    <a
                      href="mailto:support@ourprysm.com"
                      className="text-blue-400 hover:text-blue-300 text-lg font-semibold transition-colors duration-200"
                    >
                      support@ourprysm.com
                    </a>
                  </div>
                </div>

                <div className="text-center text-sm text-gray-400">
                  <p>We typically respond within 24 hours during business days.</p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeSupportModal}
                  className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all duration-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

export default Navbar; 